import React, { useEffect, useState, useRef, useMemo } from 'react'
import transactionStore from './stores/transaction'
import Pagination from './components/Pagination'
import Table from '@/../components/Table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro' // <-- import styles to be used
import Button from '@/../components/Button'
import { Text } from '@/../components/Fields'
import destinationStore from '@/../stores/destination'
import { DateTime } from 'luxon'
import Select2 from 'react-select'

export default function () {

  const [pending, setPending] = useState(true)

  const tid = useRef(null)

  const [
    collection,
    setCollection,
    total,
    setTotal,
    page,
    setPage,
    filters,
    setFilters,
  ] = transactionStore(state => [
    state.collection,
    state.setCollection,
    state.total,
    state.setTotal,
    state.page,
    state.setPage,
    state.filters,
    state.setFilters,
  ])

  const [
    destinations,
    fetchDestinations,
  ] = destinationStore(state => [
    state.collection,
    state.fetch,
  ])

  const fetchTransactions = async () => {
    const params = new URLSearchParams()
    if (filters.destination_ids) {
      params.append('destination_ids', filters.destination_ids)
    }
    if (filters.reference) {
      params.append('reference', filters.reference)
    }
    if (filters.order_name) {
      params.append('order_name', filters.order_name)
    }
    if (filters.magento_order_id) { 
      params.append('magento_order_id', filters.magento_order_id)
    }
    params.append('page', page)
    params.append('pending', pending ? '1' : '0')
    const response = await fetch(`/api/inventory_transactions/ops.json?${params.toString()}`)
    const data = await response.json()
    setCollection(data.collection)
    setTotal(data.total)
  }

  useEffect(() => {
    fetchTransactions()
  }, [page, pending])

  useEffect(() => {
    if (tid.current) {
      clearTimeout(tid.current)
    }
    tid.current = setTimeout(() => {
      fetchTransactions()
    }, 500)
    return () => {
      clearTimeout(tid.current)
    }
  }, [filters])

  useEffect(() => {
    fetchDestinations()
  }, [])

  useEffect(() => {
    setPage(1)
  }, [pending])

  return <>
    <div className="flex justify-between gap-4 mb-4 items-center mb-4">
      <div className='flex gap-4'>
        <h1 className="text-4xl">OPS</h1>
      </div>
      <div className="grow"></div>
      <div className="flex gap-4">
        <label className="flex items-center">
          <input
            type="radio"
            name="pending"
            checked={pending}
            onChange={e => setPending(true)}
          />
          <span className="ml-2">Trip Codes Missing</span>
        </label>
        <label className="flex items-center">
          <input
            type="radio"
            name="pending"
            checked={!pending}
            onChange={e => setPending(false)}
          />
          <span className="ml-2">Trip Codes Completed</span>
        </label>
      </div>
      <Pagination className="mb-0" {...{total, page, setPage}} />
    </div>
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.Th>ID</Table.Th>
          <Table.Th>Date</Table.Th>
          <Table.Th>Products</Table.Th>
          <Table.Th>Destinations</Table.Th>
          <Table.Th width="120">Product count</Table.Th>
          <Table.Th>Magento Order #</Table.Th>
          <Table.Th>Order Name</Table.Th>
          <Table.Th width="104"></Table.Th>
        </Table.Row>
        <Table.Row>
          <Table.Th></Table.Th>
          <Table.Th></Table.Th>
          <Table.Th></Table.Th>
          <Table.Th width="400" className="text-left">
            <Select
              onChange={destinations => setFilters({ destination_ids: destinations?.map(d => d.value).join(',') })}
              value={filters.destination_ids}
            />
          </Table.Th>
          <Table.Th></Table.Th>
          <Table.Th>
            <Text
              label={false}
              value={filters.magento_order_id}
              onChange={e => setFilters({ magento_order_id: e.target.value })}
            />
          </Table.Th>
          <Table.Th>
            <Text
              label={false}
              value={filters.order_name}
              onChange={e => setFilters({ order_name: e.target.value })}
            />
          </Table.Th>
          <Table.Th></Table.Th>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {collection.map(transaction => (
          <TransactionRow key={transaction.id} {...{transaction}} />
        ))}
      </Table.Body>
    </Table>
  </>
}

const TransactionRow = ({ transaction }) => {

  const pencil = icon({ name: 'pencil' })

  const [
    destinations,
  ] = destinationStore(state => [
    state.collection,
  ])

  return (
    <Table.Row className={transaction.destinations_transactions_attributes.length > 0 ? 'bg-green-50' : 'bg-red-50'}>
      <Table.Td>{transaction.id}</Table.Td>
      <Table.Td>{DateTime.fromISO(transaction.created_at).toLocaleString(DateTime.DATE_MED)}</Table.Td>
      <Table.Td>
        <ul className="list-disc pl-6">
          {transaction.items_attributes && transaction.items_attributes.map((item) =>
            <li key={item.id}>{item.magento_id} - {item.quantity} - {item.title}</li>
          )}
        </ul>
      </Table.Td>
      <Table.Td>
        <ul className="list-disc pl-6">
          {transaction.destinations_transactions_attributes && transaction.destinations_transactions_attributes.map((item) => {
            const destination = destinations.find(d => d.id == item.destination_id)
            return <li key={item.id}><span className="font-bold">{destination?.financeCategory}</span>: {item.percentage}%</li>
          })}
        </ul>
      </Table.Td>
      <Table.Td className="text-right">{transaction.items_attributes.reduce((acc, i) => acc + i.quantity, 0)}</Table.Td>
      <Table.Td>{transaction.magento_order_id}</Table.Td>
      <Table.Td>{transaction.order_name}</Table.Td>
      <Table.Td>
        <Button to={`${transaction.id}/edit`}>
          <FontAwesomeIcon icon={pencil} />
          Edit
        </Button>
      </Table.Td>
    </Table.Row>
  )
}

function Select({onChange, value}) {

  const [defaultDestination, setDefaultDestination] = useState(null)
  const [allDestinations, fetchDestinations] = destinationStore(state => [state.collection, state.fetch])

  useEffect(() => {
    fetchDestinations()
  }, [fetchDestinations])

  const destinations = useMemo(() => {
    if (!allDestinations) return []
    return allDestinations.filter(d => {
      return !d.isArchived.toString().includes('1')
    })
  }, [allDestinations])

  useEffect(() => {
    if (!destinations) return
    setDefaultDestination(value?.split(',')?.map(v => destinations.find(d => d.value == v)))
  }, [destinations, value])

  return (
    <div>
      <Select2
        isClearable
        options={destinations}
        onChange={onChange}
        menuPlacement="top"
        value={defaultDestination}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        isMulti
      />
    </div>
  )
}
